import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSession } from "../lib/auth.ts";
import { fmtDurationMs, api, formatSerial, testResultLabel, testResultPillClass, testTypeLabel, warrantyPillClass, warrantyLabel } from "../lib/api.ts";
import { ChartIcon, EmptyState } from "../components/EmptyState.tsx";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

export function DeviceDetailPage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const nav = useNavigate();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ["device", orgId, deviceId],
    queryFn: () => api.deviceDetail(orgId!, deviceId!),
    enabled: !!orgId && !!deviceId,
  });

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [renameErr, setRenameErr] = useState<string | null>(null);

  const renameMutation = useMutation({
    mutationFn: (name: string) => api.updateDevice(orgId!, deviceId!, name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["device", orgId, deviceId] });
      qc.invalidateQueries({ queryKey: ["devices", orgId] });
      setEditing(false);
      setRenameErr(null);
    },
    onError: (e: Error) => setRenameErr(e.message),
  });

  const tests = useQuery({
    queryKey: ["tests", orgId, "device", deviceId, 200],
    queryFn: () => api.listTests(orgId!, 200, deviceId),
    enabled: !!orgId && !!deviceId,
  });
  const sortable = useSortableTable(tests.data?.tests ?? [], {
    startedAt:  (t) => t.startedAt,
    testType:   (t) => testTypeLabel(t.testType),
    testResult: (t) => testResultLabel(t.testResult),
    durationMs: (t) => t.durationMs,
  }, "startedAt", "desc");

  if (detail.isLoading) return <p className="muted">Loading…</p>;
  if (detail.error) return <p className="err">Couldn't load device — {(detail.error as Error).message}</p>;
  if (!detail.data) return null;
  const d = detail.data.device;

  return (
    <>
      <Link to="/devices">← Back to devices</Link>
      {editing ? (
        <form
          style={{ display: "flex", gap: "0.5rem", alignItems: "center", margin: "1rem 0" }}
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = draftName.trim();
            if (!trimmed) {
              setRenameErr("Name can't be empty");
              return;
            }
            renameMutation.mutate(trimmed);
          }}
        >
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            maxLength={200}
            style={{ fontSize: "1.4em", padding: "0.3em 0.5em", flex: 1 }}
          />
          <button type="submit" disabled={renameMutation.isPending}>
            {renameMutation.isPending ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setRenameErr(null);
            }}
            disabled={renameMutation.isPending}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
          <h1 style={{ margin: "0.6em 0" }}>{d.name ?? d.assetSerial ?? formatSerial(d.serial)}</h1>
          <button
            type="button"
            onClick={() => {
              setDraftName(d.name ?? d.assetSerial ?? formatSerial(d.serial));
              setEditing(true);
              setRenameErr(null);
            }}
            style={{ padding: "0.3em 0.7em", fontSize: "0.9em" }}
          >
            Rename
          </button>
        </div>
      )}
      {renameErr && <p className="err">Couldn't rename — {renameErr}</p>}

      <div className="card">
        <div className="kv-grid">
          <span className="muted">Cloud device id</span>
          <code>{d.id}</code>

          <span className="muted">Asset serial</span>
          {d.assetSerial
            ? <code>{d.assetSerial}</code>
            : <span className="muted">—</span>}

          <span className="muted">Warranty</span>
          {detail.data.warranty
            ? (
              <span>
                <span className={warrantyPillClass(detail.data.warranty.state)}>
                  {warrantyLabel(detail.data.warranty.state)}
                </span>
                {detail.data.warranty.expires_at
                  ? ` · expires ${new Date(detail.data.warranty.expires_at).toLocaleDateString()}`
                  : ""}
              </span>
            )
            : <span className="muted">—</span>}

          <span className="muted">Hardware serial</span>
          <code>{formatSerial(d.serial)}</code>

          <span className="muted">Update channel</span>
          <span>{d.updateChannel}</span>

          <span className="muted">Firmware version</span>
          <span>{d.firmwareVersion ?? <span className="muted">—</span>}</span>

          <span className="muted">Paired at</span>
          <span>{new Date(d.pairedAt).toLocaleString()}</span>

          <span className="muted">Last seen</span>
          <span>{d.lastSeen ? new Date(d.lastSeen).toLocaleString() : <span className="muted">never</span>}</span>
        </div>
      </div>

      <h3>Tests from this device</h3>
      {tests.isLoading && <p className="muted">Loading…</p>}
      {tests.error && <p className="err">Couldn't load tests — {(tests.error as Error).message}</p>}
      {tests.data && tests.data.tests.length === 0 && (
        <EmptyState
          icon={<ChartIcon />}
          title="No tests yet"
          message="Run a test on this device — results auto-sync once the test completes."
        />
      )}
      {tests.data && tests.data.tests.length > 0 && (
        <table>
          <thead>
            <tr>
              <SortHeader label="When"     columnKey="startedAt"  sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Type"     columnKey="testType"   sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Result"   columnKey="testResult" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Duration" columnKey="durationMs" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
            </tr>
          </thead>
          <tbody>
            {sortable.sorted.map((t) => (
              <tr key={t.id} onClick={() => nav(`/tests/${t.id}`)}>
                <td>{new Date(t.startedAt).toLocaleString()}</td>
                <td>{testTypeLabel(t.testType)}</td>
                <td>
                  <span className={testResultPillClass(t.testResult)}>
                    {testResultLabel(t.testResult)}
                  </span>
                </td>
                <td>{fmtDurationMs(t.durationMs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
